import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';

import React, { useEffect, createContext, useContext } from 'react';
import {
    Container,
    Drawer,
    ListItem,
    SvgIcon,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext, getAppUser, setAppUser } from '../contexts/AppContext';
import { Trans, useTranslation } from 'react-i18next';
import {
    createFullLayoutStyles,
    LeftMenuItemButton,
} from './FullLayoutStyles';
import { useSharedContext } from '../contexts/SharedContext';
import { changeLanguage, getLanguage } from '../lib/i18n';
import { paths } from '../utils/paths';
import { AppTheme } from '../theme';
import { MenuApp } from '../lib/commonStyles';
import IconLib from '../lib/icons/IconLib';
import ConfirmationDialog from '../lib/components/ConfirmationDialog/ConfirmationDialog';


interface FullLayoutContextType {
    guttersEnabled: boolean;
    leftMenuExpanded: boolean;
    setGuttersEnabled: (value: boolean) => void;
    setLeftMenuExpanded: (value: boolean) => void;
}

export const FullLayoutContext = createContext<FullLayoutContextType | null>(null);

export const useFullLayout = () => {
    const context = useContext(FullLayoutContext);
    if (!context) throw new Error('useLayout must be used within LayoutProvider');
    return context;
};

const modifiedPaths: string[] = [
    paths.mapViewer,
    paths.baselineEnvironmentCompleted,
    paths.newBaselineEnvironment,
    paths.baselineEnvironment
]

function FullLayout(props: any) {
    const theme: AppTheme = useTheme();
    const styles = createFullLayoutStyles(theme);
    const navigate = useNavigate();
    const location = useLocation();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [confirmLogout, setConfirmLogout] = React.useState(false);

    const [t] = useTranslation();

    const sharedContext = useSharedContext();

    const { logout, setMenuIndex, menuIndex, darkTheme, setDarkTheme, userPerms } = useAppContext();

    // UI
    const [guttersEnabled, setGuttersEnabled] = React.useState(true);
    const [leftMenuExpanded, setLeftMenuExpanded] = React.useState(true);
    const isModifiedPath = modifiedPaths.some(path => {
        const match = matchPath({ path, end: true }, location.pathname)
        if (!match) return false;
        // Excluimos 'list' porque lo lee como param
        if (match.params?.baselineEnvironmentUid === 'list') return false;
        return true;
    });

    useEffect(() => {
        if (isMobile || isDesktop) {
            handleLeftMenu(true);
            return
        }
        handleLeftMenu(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, isDesktop])

    useEffect(() => {
        if (isModifiedPath) {
            handleGutters(true);
            handleLeftMenu(false);
        } else {
            handleGutters(false);
            handleLeftMenu(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])


    const handleGutters = (value: boolean) => {
        if (value === guttersEnabled) return;
        setGuttersEnabled(value);
    }

    const handleLeftMenu = (value: boolean) => {
        setLeftMenuExpanded(value);
    }

    const toggleLeftMenu = () => {
        if (isMobile) return;
        setLeftMenuExpanded(prev => !prev); 
    };
    
    const handleDrawerOpen = () => {
        setOpenDrawer(true);
    };

    const handleDrawerClose = () => {
        setOpenDrawer(false);
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        handleMobileMenuClose();
    };

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const handleChangePassword = () => {
        goto(paths.changePassword, -1);
        handleMobileMenuClose();
        handleMenuClose();
    };

    const handleChangeDarkMode = () => {
        setDarkTheme(!darkTheme);
        handleMobileMenuClose();
        handleMenuClose();
    };

    const goto = (rute: string, index: number) => {
        setMenuIndex(index);
        sharedContext.setInitialSharedData();
        navigate(rute);
        handleDrawerClose();
    };

    const closeConfirmLogout = () => {
        setConfirmLogout(false);
    }

    const openConfirmLogout = () => {
        setConfirmLogout(true);
    }
    
    const handleLogout = () => {
        logout();
        setAppUser(null);
    };

    const menuId = 'primary-account-menu';
    const renderMenu = (
        <MenuApp
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            id={menuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            <MenuItem sx={{"&.MuiMenuItem-root": {minHeight: "60px"}}}>
                <Typography variant="body2" sx={{color: theme.palette.neutral_2}}>{getAppUser()?.email}</Typography>
            </MenuItem>
            <Divider sx={{ backgroundColor: theme.palette.neutral_3 }}/>
            <MenuItem onClick={handleChangePassword} sx={{"&.MuiMenuItem-root": {minHeight: "60px"}}}>
                <SvgIcon component={IconLib.lockSvg} inheritViewBox title="" sx={{ fontWeight: "bold"}}></SvgIcon>
                <Typography variant="body2" sx={{pl: "20px"}}>{t('LAYOUT.CHANGE_PASSWORD')}</Typography>
            </MenuItem>
            <MenuItem onClick={() => changeLanguage()} sx={{"&.MuiMenuItem-root": {minHeight: "60px"}}}>
                <SvgIcon component={IconLib.chatAppSvg} inheritViewBox title="" sx={{ fontWeight: "bold"}}></SvgIcon>
                <Typography variant="body2" sx={{pl: "20px"}}>{t('COMMON.CHANGE_LANGUAGE')}</Typography>       
            </MenuItem>
            {/*
            <MenuItem onClick={handleChangeDarkMode} sx={{"&.MuiMenuItem-root": {minHeight: "60px"}}}>
                <SvgIcon component={IconLib.sunAndMoonSvg} inheritViewBox title="" sx={{ fontWeight: "bold"}}></SvgIcon>
                <Typography variant="body2" sx={{pl: "20px"}}>{darkTheme ? t('LAYOUT.LIGHT_MODE') : t('LAYOUT.DARK_MODE')}</Typography>
            </MenuItem>
            */}
        </MenuApp>
    );

    const mobileMenuId = 'primary-account-menu-mobile';
    const renderMobileMenu = (
        <MenuApp
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            <MenuItem sx={{"&.MuiMenuItem-root": {minHeight: "60px"}}}>
                <Box sx={{mt: "auto", mb: "auto"}}>
                    <Typography variant="body2" sx={{color: theme.palette.neutral_2}}>
                        {getAppUser()?.last_name}
                        {( getAppUser()?.first_name != null && (getAppUser()?.first_name + "").trim().length > 1) && ", "}
                        {getAppUser()?.first_name}
                    </Typography>
                    <Typography variant="body2" sx={{color: theme.palette.neutral_2}}>
                        {getAppUser()?.email}
                    </Typography>
                </Box>
            </MenuItem>
            <Divider sx={{ backgroundColor: theme.palette.neutral_3 }}/>
            <MenuItem onClick={handleChangePassword} sx={{"&.MuiMenuItem-root": {minHeight: "60px"}}}>
                <SvgIcon component={IconLib.lockSvg} inheritViewBox title="" sx={{ color:theme.palette.neutral_0, fontWeight: "bold"}}></SvgIcon>
                <Typography variant="body2" sx={{pl: "20px"}}>{t('LAYOUT.CHANGE_PASSWORD')}</Typography>
            </MenuItem>
            {getLanguage() === 'en' && (
                <MenuItem onClick={() => changeLanguage()} sx={{"&.MuiMenuItem-root": {minHeight: "60px"}}}>
                    <SvgIcon component={IconLib.chatAppSvg} inheritViewBox title="" sx={{ color:theme.palette.neutral_0, fontWeight: "bold"}}></SvgIcon>
                    <Typography variant="body2" sx={{pl: "20px"}}>Español</Typography>       
                </MenuItem>
            )}
            {getLanguage() === 'es' && (
                <MenuItem onClick={() => changeLanguage()} sx={{"&.MuiMenuItem-root": {minHeight: "60px"}}}>
                    <SvgIcon component={IconLib.chatAppSvg} inheritViewBox title="" sx={{ color:theme.palette.neutral_0, fontWeight: "bold"}}></SvgIcon>
                    <Typography variant="body2" sx={{pl: "20px"}}>English</Typography>    
                </MenuItem>
            )}
            {/*
            <MenuItem onClick={handleChangeDarkMode} sx={{"&.MuiMenuItem-root": {minHeight: "60px"}}}>
                <SvgIcon component={IconLib.sunAndMoonSvg} inheritViewBox title="" sx={{ fontWeight: "bold"}}></SvgIcon>
                <Typography variant="body2" sx={{pl: "20px"}}>{darkTheme ? t('LAYOUT.LIGHT_MODE') : t('LAYOUT.DARK_MODE')}</Typography>
            </MenuItem>
            */}
        </MenuApp>
    );

    const renderLeftMenu = (
        <React.Fragment>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "32px" }}>
                <Box sx={{ backgroundColor: "white", position: "sticky", top: 0, zIndex: 999}}>
                    <Box component={"p"} sx={[leftMenuExpanded ? styles.logo : styles.logoTablet ]} onClick={toggleLeftMenu}></Box>
                </Box>
                <Box component={"nav"} aria-label="menu">
                    <List sx={[styles.menu]}>
                        <ListItem alignItems="flex-start" disableGutters>
                            <LeftMenuItemButton 
                                selected={menuIndex === 0}
                                onClick={(event) => goto(paths.home, 0)}
                                sx={{flexGrow: leftMenuExpanded ? 1 : 0}}
                            >
                                <ListItemIcon sx={{ minWidth: leftMenuExpanded ? "33px" : "auto" }}>
                                    {menuIndex === 0 ?
                                    <SvgIcon component={IconLib.gridLayoutSvg} inheritViewBox title={<Trans>MENU.HOME</Trans>} sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_5}}></SvgIcon>
                                    :
                                    <SvgIcon component={IconLib.gridLayoutSvg} inheritViewBox title={<Trans>MENU.HOME</Trans>} sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_0, fontWeight: "bold"}}></SvgIcon>
                                    }
                                    
                                </ListItemIcon>
                                {leftMenuExpanded && (menuIndex === 0 ? 
                                <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_5}}>{t('MENU.HOME')}</Typography>} />
                                :
                                <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_0}}>{t('MENU.HOME')}</Typography>} />
                                )}
                            </LeftMenuItemButton>
                        </ListItem>
                        { userPerms?.productive_unit_view_productiveunit &&
                            <ListItem alignItems="flex-start" disableGutters>
                                <LeftMenuItemButton 
                                    selected={menuIndex === 1}
                                    onClick={(event) => goto(paths.productiveUnits, 1)}
                                    sx={{flexGrow: leftMenuExpanded ? 1 : 0}}
                                >
                                    <ListItemIcon sx={{ minWidth: leftMenuExpanded ? "33px" : "auto" }}>
                                        {menuIndex === 1 ?
                                        <SvgIcon component={IconLib.tractorSvg} inheritViewBox title={<Trans>PRODUCTIVE_UNIT.PRODUCTIVE_UNITS</Trans>} sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_5 }}></SvgIcon>
                                        :
                                        <SvgIcon component={IconLib.tractorSvg} inheritViewBox title={<Trans>PRODUCTIVE_UNIT.PRODUCTIVE_UNITS</Trans>} sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_0, fontWeight: "bold"}}></SvgIcon>
                                        }
                                        
                                    </ListItemIcon>
                                    {leftMenuExpanded && (menuIndex === 1 ?
                                    <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_5}}>{t('PRODUCTIVE_UNIT.PRODUCTIVE_UNITS')}</Typography>} />
                                    :
                                    <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_0}}>{t('PRODUCTIVE_UNIT.PRODUCTIVE_UNITS')}</Typography>} />
                                    )}
                                    
                                </LeftMenuItemButton>
                            </ListItem>
                        }
                        { userPerms?.event_view_event &&
                            <ListItem alignItems="flex-start" disableGutters>
                                <LeftMenuItemButton 
                                    selected={menuIndex === 2}
                                    onClick={(event) => goto(paths.events, 2)}
                                    sx={{flexGrow: leftMenuExpanded ? 1 : 0}}
                                >
                                    <ListItemIcon sx={{ minWidth: leftMenuExpanded ? "33px" : "auto" }}>
                                        {menuIndex === 2 ?
                                        <SvgIcon component={IconLib.contentPostSvg} inheritViewBox title={<Trans>EVENT.EVENTS</Trans>} sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_5}}></SvgIcon>
                                        :
                                        <SvgIcon component={IconLib.contentPostSvg} inheritViewBox title={<Trans>EVENT.EVENTS</Trans>} sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_0, fontWeight: "bold"}}></SvgIcon>
                                        }
                                        
                                    </ListItemIcon>
                                    {leftMenuExpanded && (menuIndex === 2 ?
                                    <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_5}}>{t('EVENT.EVENTS')}</Typography>} />
                                    :
                                    <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_0}}>{t('EVENT.EVENTS')}</Typography>} />
                                    )}
                                </LeftMenuItemButton>
                            </ListItem>
                        }
                        { userPerms?.productive_unit_view_harvest &&
                            <ListItem alignItems="flex-start" disableGutters>
                                <LeftMenuItemButton 
                                    selected={menuIndex === 3}
                                    onClick={(event) => goto(paths.harvests, 3)}
                                    sx={{flexGrow: leftMenuExpanded ? 1 : 0}}
                                >
                                    <ListItemIcon sx={{ minWidth: leftMenuExpanded ? "33px" : "auto" }}>
                                        {menuIndex === 3 ?
                                        <SvgIcon component={IconLib.agendaSvg} inheritViewBox title={<Trans>PRODUCTIVE_UNIT.HARVESTS</Trans>} sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_5}}></SvgIcon>
                                        :
                                        <SvgIcon component={IconLib.agendaSvg} inheritViewBox title={<Trans>PRODUCTIVE_UNIT.HARVESTS</Trans>} sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_0, fontWeight: "bold"}}></SvgIcon>
                                        }
                                        
                                    </ListItemIcon>
                                    {leftMenuExpanded && (menuIndex === 3 ?
                                    <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_5}}>{t('PRODUCTIVE_UNIT.HARVESTS')}</Typography>} />
                                    :
                                    <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_0}}>{t('PRODUCTIVE_UNIT.HARVESTS')}</Typography>} />
                                    )}
                                </LeftMenuItemButton>
                            </ListItem>
                        }
                        { userPerms?.auth_can_view_reports &&
                            <ListItem alignItems="flex-start" disableGutters>
                                <LeftMenuItemButton
                                    selected={menuIndex === 4}
                                    onClick={(event) => goto(paths.reports, 4)}
                                    sx={{ flexGrow: leftMenuExpanded ? 1 : 0 }}
                                >
                                    <ListItemIcon sx={{ minWidth: leftMenuExpanded ? "33px" : "auto" }}>
                                        {menuIndex === 4 ?
                                            <SvgIcon component={IconLib.businessReportSvg} inheritViewBox title={<Trans>REPORT.REPORTS</Trans>} sx={{ fontSize: leftMenuExpanded ? "24px" : "16.75px", color: theme.palette.neutral_5 }}></SvgIcon>
                                            :
                                            <SvgIcon component={IconLib.businessReportSvg} inheritViewBox title={<Trans>REPORT.REPORTS</Trans>} sx={{ fontSize: leftMenuExpanded ? "24px" : "16.75px", color: theme.palette.neutral_0, fontWeight: "bold" }}></SvgIcon>
                                        }
                                    
                                    </ListItemIcon>
                                    {leftMenuExpanded && (menuIndex === 4 ?
                                        <ListItemText disableTypography primary={<Typography variant="body1" sx={{ color: theme.palette.neutral_5 }}>{t('REPORT.REPORTS')}</Typography>} />
                                        :
                                        <ListItemText disableTypography primary={<Typography variant="body1" sx={{ color: theme.palette.neutral_0 }}>{t('REPORT.REPORTS')}</Typography>} />
                                    )}
                                </LeftMenuItemButton>
                            </ListItem>
                        }
                        { userPerms?.auth_view_map_viewer &&
                            <ListItem alignItems="flex-start" disableGutters>
                                <LeftMenuItemButton
                                    selected={menuIndex === 5}
                                    onClick={(event) => goto(paths.mapViewer, 5)}
                                    sx={{ flexGrow: leftMenuExpanded ? 1 : 0 }}
                                >
                                    <ListItemIcon sx={{ minWidth: leftMenuExpanded ? "33px" : "auto" }}>
                                        {menuIndex === 5 ?
                                            <SvgIcon component={IconLib.gpsMapSvg} inheritViewBox title={<Trans>MAP_VIEWER.MAP_VIEWER</Trans>} sx={{ fontSize: leftMenuExpanded ? "24px" : "16.75px", color: theme.palette.neutral_5 }}></SvgIcon>
                                            :
                                            <SvgIcon component={IconLib.gpsMapSvg} inheritViewBox title={<Trans>MAP_VIEWER.MAP_VIEWER</Trans>} sx={{ fontSize: leftMenuExpanded ? "24px" : "16.75px", color: theme.palette.neutral_0, fontWeight: "bold" }}></SvgIcon>
                                        }
                                    
                                    </ListItemIcon>
                                    {leftMenuExpanded && (menuIndex === 5 ?
                                        <ListItemText disableTypography primary={<Typography variant="body1" sx={{ color: theme.palette.neutral_5 }}>{t('MAP_VIEWER.MAP_VIEWER')}</Typography>} />
                                        :
                                        <ListItemText disableTypography primary={<Typography variant="body1" sx={{ color: theme.palette.neutral_0 }}>{t('MAP_VIEWER.MAP_VIEWER')}</Typography>} />
                                    )}
                                </LeftMenuItemButton>
                            </ListItem>
                        }
                        { userPerms?.environment_view_baselineenvironment &&
                            <ListItem alignItems="flex-start" disableGutters>
                                <LeftMenuItemButton 
                                    selected={menuIndex === 6}
                                    onClick={(event) => goto(paths.baselineEnvironments, 6)}
                                    sx={{flexGrow: leftMenuExpanded ? 1 : 0}}
                                >
                                    <ListItemIcon sx={{ minWidth: leftMenuExpanded ? "33px" : "auto" }}>
                                        {menuIndex === 6 ?
                                        <SvgIcon component={IconLib.paperStackSvg} inheritViewBox sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_5}}></SvgIcon>
                                        :
                                        <SvgIcon component={IconLib.paperStackSvg} inheritViewBox sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color:theme.palette.neutral_0, fontWeight: "bold"}}></SvgIcon>
                                        }
                                        
                                    </ListItemIcon>
                                    {leftMenuExpanded && (menuIndex === 6 ?
                                    <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_5}}>{t('ENVIRONMENT.ENVIRONMENTS')}</Typography>} />
                                    :
                                    <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.neutral_0}}>{t('ENVIRONMENT.ENVIRONMENTS')}</Typography>} />
                                    )}
                                </LeftMenuItemButton>
                            </ListItem>
                        }
                    </List>
                </Box>
            </Box>
            <Box component={"nav"} aria-label="menu" sx={{ height:"100%", display:"flex", flexDirection:"column", justifyContent:"center" }}>
                <List sx={[styles.menu]}>
                    <ListItem alignItems="flex-start" disableGutters>
                        <LeftMenuItemButton
                            data-testid="logout-button" 
                            selected={menuIndex === 99}
                            onClick={(event) => openConfirmLogout()}
                            sx={{flexGrow: leftMenuExpanded ? 1 : 0}}
                        >
                            <ListItemIcon sx={{ minWidth: leftMenuExpanded ? "33px" : "auto" }}>
                                <SvgIcon component={IconLib.logoutSvg} inheritViewBox sx={{fontSize: leftMenuExpanded ? "24px" : "16.75px", color: theme.palette.error.main}}></SvgIcon>
                            </ListItemIcon>
                            {leftMenuExpanded &&
                                <ListItemText disableTypography primary={<Typography variant="body1" sx={{color:theme.palette.error.main}}>{t('LAYOUT.CLOSE_SESSION')}</Typography>} sx={{fontWeight: 600, color:theme.palette.error.main }}/>
                            }
                        </LeftMenuItemButton>
                    </ListItem>
                </List>
            </Box>
        </React.Fragment>
    );

    return (
        <FullLayoutContext value={{
            guttersEnabled,
            leftMenuExpanded,
            setGuttersEnabled,
            setLeftMenuExpanded
        }}>
            <Box sx={{ display: 'flex' }}>
                <Box sx={[styles.gridContainer]}>
                    <Box sx={[styles.leftPanel, { width: leftMenuExpanded ? "100%" : "unset"}]}>
                        {renderLeftMenu}
                    </Box>
                    <Drawer
                        open={openDrawer}
                        onClose={handleDrawerClose}
                        sx={{ '& .MuiPaper-root': { minWidth: 250 } }}
                    >
                        <React.Fragment>
                            <Box sx={[styles.leftPanelMobile]}>
                                {renderLeftMenu}
                            </Box>
                        </React.Fragment>
                    </Drawer>
                    <Box sx={[styles.rightPanel]}>
                        <Box sx={[styles.header]}>
                            <Box sx={{display: 'flex', alignSelf: "center"}}>
                                <Box sx={{display: {xs: 'flex', sm: 'none', md: 'none', lg: 'none'}}}>
                                    <IconButton edge="start" sx={{ mr: theme.spacing(2) }} color="inherit" aria-label="menu" onClick={handleDrawerOpen}>
                                        <SvgIcon component={IconLib.menuFullSvg} inheritViewBox sx={{ color: theme.palette.primary.light}}></SvgIcon>
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box sx={{display: {xs: 'none', sm: 'none', md: 'flex'}, cursor: "pointer", alignSelf: "flex-end", height: "100%"}} onClick={handleProfileMenuOpen}>
                                <Box sx={{mt: "auto", mb: "auto"}}>
                                    <Box sx={{textAlign: 'right'}}>
                                        {getAppUser()?.last_name}
                                        {( getAppUser()?.first_name != null && (getAppUser()?.first_name + "").trim().length > 1) && ", "}
                                        {getAppUser()?.first_name}
                                    </Box>
                                    <Box sx={{textAlign: 'right', fontWeight:"bold", fontSize: "14px"}}>{getAppUser()?.email}</Box>
                                </Box>
                                <Box sx={{width: "60px", textAlign: "center", pt: "5px", mt: "auto", mb: "auto"}}><SvgIcon color="primary" title='' component={IconLib.smileySvg} inheritViewBox sx={{fontSize:"30px"}}></SvgIcon></Box>
                            </Box>
                            <Box sx={{display: {xs: 'flex', sm: 'none', md: 'none'}}}>
                                <IconButton
                                    aria-label="show more"
                                    aria-controls={mobileMenuId}
                                    aria-haspopup="true"
                                    onClick={handleMobileMenuOpen}
                                    color="inherit"
                                >
                                    <SvgIcon color="primary" title='' component={IconLib.smileySvg} inheritViewBox sx={{fontSize:"30px"}}></SvgIcon>
                                </IconButton>
                            </Box>
                            {renderMobileMenu}
                            {renderMenu}
                        </Box>
                        <Box
                            component={'main'}
                            sx={[styles.main]}
                        >
                            <Container sx={[guttersEnabled ? {height:"100%", maxWidth:"100% !important", p:"0 !important", m:"0 !important"} : styles.content ]} >
                                {props.children}
                            </Container>
                        </Box>
                    </Box>
                </Box>

            </Box>
            <ConfirmationDialog open={confirmLogout} onClose={closeConfirmLogout} onConfirm={handleLogout} title={t("LAYOUT.CLOSE_SESSION")} subtitle={t("LAYOUT.MSJ_CONF_CLOSE_SESSION")}/>
        </FullLayoutContext>
    );
}

export default FullLayout;
